// # Filename: src/components/games/DebugViewportOverlay.jsx
//
// ============================================================================
// TEMPORARY DEBUG TOOL -- delete this file, and its two-line usage in
// SudokuPage.jsx, once the mobile Safari viewport investigation is done.
// ============================================================================
//
// Only renders when `?debug=1` is in the URL. Shows live window.visualViewport
// numbers plus GameBoardFrame's/the header's actual rendered getBoundingClientRect(),
// so real iOS Safari values can be compared against what a headless-Chromium test
// reports (which can't reproduce Safari's dynamic toolbar behavior at all).
//
// Purely observational: reads the DOM, changes nothing about layout/positioning.

import React, { useEffect, useState } from "react";

const DEBUG_QUERY_PARAM = "debug";

export function isDebugOverlayEnabled() {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get(DEBUG_QUERY_PARAM) === "1";
}

function findGameBoardFrameElement() {
  // No ref plumbing added to GameBoardFrame -- walk up from the board grid to the
  // nearest position:fixed ancestor, which is GameBoardFrame's own root div on mobile.
  const board = document.querySelector(".grid-cols-9, .grid-cols-8, [style*='grid-template-columns']");
  let el = board;
  while (el && el !== document.body) {
    if (getComputedStyle(el).position === "fixed") return el;
    el = el.parentElement;
  }
  return null;
}

function readSnapshot() {
  const vv = window.visualViewport;
  const header = document.querySelector("header");
  const headerRect = header ? header.getBoundingClientRect() : null;
  const frameEl = findGameBoardFrameElement();
  const frameRect = frameEl ? frameEl.getBoundingClientRect() : null;

  return {
    time: new Date().toLocaleTimeString(),
    visualViewport: vv
      ? { width: vv.width, height: vv.height, offsetTop: vv.offsetTop, offsetLeft: vv.offsetLeft, scale: vv.scale }
      : null,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    header: headerRect ? { top: headerRect.top, height: headerRect.height, bottom: headerRect.bottom } : null,
    frame: frameRect
      ? {
          top: frameRect.top,
          height: frameRect.height,
          bottom: frameRect.bottom,
          appliedPosition: frameEl.style.position || "(not inline: " + getComputedStyle(frameEl).position + ")",
          appliedTop: frameEl.style.top || "(none)",
          appliedHeight: frameEl.style.height || "(none)",
        }
      : null,
  };
}

export default function DebugViewportOverlay() {
  const [snapshot, setSnapshot] = useState(readSnapshot);

  useEffect(() => {
    const update = () => setSnapshot(readSnapshot());
    update();
    const vv = window.visualViewport;
    window.addEventListener("resize", update);
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    // Polling too: Safari's toolbar collapse/expand animation doesn't always fire a
    // visualViewport event at every frame, and we want to catch mid-animation values.
    const interval = window.setInterval(update, 400);
    return () => {
      window.removeEventListener("resize", update);
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
      window.clearInterval(interval);
    };
  }, []);

  const vv = snapshot.visualViewport;
  const h = snapshot.header;
  const f = snapshot.frame;

  return (
    <pre
      style={{
        position: "fixed",
        top: 4,
        right: 4,
        zIndex: 999999,
        margin: 0,
        padding: "8px 10px",
        fontSize: "10px",
        lineHeight: 1.45,
        fontFamily: "monospace",
        color: "#7CFC9A",
        background: "rgba(0,0,0,0.85)",
        border: "1px solid rgba(124,252,154,0.45)",
        borderRadius: "6px",
        maxWidth: "230px",
        whiteSpace: "pre-wrap",
        pointerEvents: "none",
      }}
    >
      {`[${snapshot.time}] DEBUG
visualViewport:
  w=${vv?.width ?? "n/a"} h=${vv?.height ?? "n/a"}
  offsetTop=${vv?.offsetTop ?? "n/a"}
  offsetLeft=${vv?.offsetLeft ?? "n/a"}
  scale=${vv?.scale ?? "n/a"}
window.inner:
  w=${snapshot.innerWidth} h=${snapshot.innerHeight}
header rect:
  top=${h?.top ?? "n/a"} h=${h?.height ?? "n/a"}
  bottom=${h?.bottom ?? "n/a"}
frame rect:
  top=${f?.top ?? "NOT FOUND"} h=${f?.height ?? "n/a"}
  bottom=${f?.bottom ?? "n/a"}
frame applied style:
  position=${f?.appliedPosition ?? "n/a"}
  top=${f?.appliedTop ?? "n/a"}
  height=${f?.appliedHeight ?? "n/a"}`}
    </pre>
  );
}
